import React, { useState, useEffect } from "react";
import { T } from "../../app/tokens.js";
import { Modal } from "../../components/Modal.jsx";
import { OkrStatusBadge } from "./OkrStatusBadge.jsx";

export function OkrInitiativeUpdatesModal({ isOpen, onClose, initiative, initialMonth, selectedYear, currentUser, currentRole, onSave }) {
  const year = selectedYear || new Date().getUTCFullYear();
  const defaultMonth = initialMonth || `${year}-${new Date().toISOString().slice(5, 7)}`;
  const [monthInput, setMonthInput] = useState(defaultMonth);
  const [text, setText] = useState("");
  const [nextSteps, setNextSteps] = useState("");
  const [status, setStatus] = useState("");
  const [percent, setPercent] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isDigestMode = !Array.isArray(initiative?.updates);
  const updates = isDigestMode
    ? (initiative?.update ? [initiative.update] : [])
    : [...(initiative?.updates || [])].sort((a, b) => b.month.localeCompare(a.month));

  const canEdit = currentRole === "admin" || currentRole === "editor" || initiative?.ownerId === currentUser?.id;

  const existingForInput = updates.find(u => u.month === monthInput);

  useEffect(() => {
    if (isOpen) {
      setMonthInput(canEdit ? defaultMonth : updates[0]?.month || defaultMonth);
      setError(null);
    }
  // Only reset when opening/switching initiatives; history selection remains local.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initiative?.id, defaultMonth, canEdit]);

  useEffect(() => {
    if (existingForInput) {
      setText(existingForInput.text || "");
      setNextSteps(existingForInput.nextSteps || "");
      setStatus(existingForInput.status || "");
      setPercent(existingForInput.percent != null ? existingForInput.percent : "");
    } else {
      setText("");
      setNextSteps("");
      setStatus("");
      setPercent("");
    }
  }, [monthInput, existingForInput]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await onSave(initiative.id, monthInput, {
        text,
        nextSteps,
        status: status || null,
        percent: percent === "" ? null : Number(percent)
      });
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!initiative) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Updates: ${initiative.title}`} maxWidth={isDigestMode ? 500 : 700}>
      <div style={{ display: "flex", minHeight: isDigestMode ? "auto" : 400, height: isDigestMode ? "auto" : "60vh" }}>
        {/* History Sidebar */}
        {!isDigestMode && (
          <div style={{ width: 220, borderRight: `1px solid ${T.border}`, overflowY: "auto", background: T.bg, display: "flex", flexDirection: "column" }}>
            <div style={{ padding: "12px 16px", fontSize: T.fsSm, fontWeight: T.fwSemibold, borderBottom: `1px solid ${T.border}` }}>History</div>
            {updates.length === 0 ? (
              <div style={{ padding: 16, fontSize: T.fsXs, color: T.mid }}>No updates yet.</div>
            ) : (
              updates.map(u => (
                <button 
                  key={u.id || u.month}
                  onClick={() => setMonthInput(u.month)}
                  style={{ 
                    display: "block", width: "100%", textAlign: "left", padding: "12px 16px", 
                    background: monthInput === u.month ? T.white : "transparent",
                    border: "none", borderBottom: `1px solid ${T.borderCompact}`, cursor: "pointer",
                    borderLeft: monthInput === u.month ? `3px solid ${T.accent}` : "3px solid transparent"
                  }}
                >
                  <div style={{ fontSize: T.fsSm, fontWeight: T.fwSemibold, color: T.dark, marginBottom: 4 }}>{u.month}</div>
                  <OkrStatusBadge status={u.status} />
                </button>
              ))
            )}
          </div>
        )}

        {/* Edit Form */}
        <div style={{ flex: 1, padding: 24, overflowY: "auto", background: T.white }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
            <h3 style={{ margin: 0, fontSize: T.fsMd, color: T.dark }}>
              {existingForInput ? "Edit Update" : "New Update"}
            </h3>
            <input 
              type="month" 
              value={monthInput} 
              onChange={e => setMonthInput(e.target.value)}
               min={`${year}-01`}
               max={`${year}-12`}
               disabled={isDigestMode || !canEdit}
              style={{ padding: "6px 10px", fontSize: T.fsSm, border: `1px solid ${T.borderDark}`, borderRadius: 4, opacity: isDigestMode ? 0.7 : 1 }}
            />
          </div>

           {!canEdit ? (
             <div style={{ fontSize: T.fsSm, color: T.dark, whiteSpace: "pre-wrap" }}>
               {existingForInput ? (
                 <>
                   <div style={{ marginBottom: 10 }}>{existingForInput.status && <OkrStatusBadge status={existingForInput.status} />}{existingForInput.percent != null && ` · ${existingForInput.percent}% snapshot`}</div>
                    <strong>Recent accomplishments</strong><div>{existingForInput.text}</div>
                    {existingForInput.nextSteps && <><strong>Next steps</strong><div>{existingForInput.nextSteps}</div></>}
                 </>
               ) : "No update for this month."}
             </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: 16 }}>
                 <label htmlFor="okr-accomplishments" style={{ display: "block", fontSize: T.fsSm, fontWeight: T.fwSemibold, color: T.dark, marginBottom: 6 }}>Recent accomplishments</label>
                <textarea 
                   id="okr-accomplishments"
                  required
                  rows={6}
                  value={text} 
                  onChange={e => setText(e.target.value)} 
                  style={{ width: "100%", padding: "10px", fontSize: T.fsBase, color: T.dark, border: `1px solid ${T.borderDark}`, borderRadius: 6, resize: "vertical" }}
                  placeholder="Monthly progress narrative..."
                />
              </div>
               <div style={{ marginBottom: 16 }}>
                 <label htmlFor="okr-next-steps" style={{ display: "block", fontSize: T.fsSm, fontWeight: T.fwSemibold, color: T.dark, marginBottom: 6 }}>Next steps (optional)</label>
                 <textarea id="okr-next-steps" rows={3} maxLength={10000} value={nextSteps}
                   onChange={e => setNextSteps(e.target.value)}
                   style={{ width: "100%", padding: "10px", fontSize: T.fsBase, color: T.dark, border: `1px solid ${T.borderDark}`, borderRadius: 6, resize: "vertical" }} />
               </div>

              <div style={{ display: "flex", gap: 16, marginBottom: 24 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: "block", fontSize: T.fsSm, fontWeight: T.fwSemibold, color: T.dark, marginBottom: 6 }}>Snapshot Status</label>
                  <select 
                    value={status} 
                    onChange={e => setStatus(e.target.value)}
                    style={{ width: "100%", padding: "8px 12px", fontSize: T.fsBase, color: T.dark, border: `1px solid ${T.borderDark}`, borderRadius: 6, appearance: "none", background: T.white }}
                  >
                    <option value="">Not recorded</option>
                    <option value="on_track">On Track</option>
                    <option value="at_risk">At Risk</option>
                    <option value="off_track">Off Track</option>
                    <option value="complete">Complete</option>
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: "block", fontSize: T.fsSm, fontWeight: T.fwSemibold, color: T.dark, marginBottom: 6 }}>Snapshot Percent (Optional)</label>
                  <input 
                    type="number" 
                    min={0} max={100}
                    value={percent} 
                    onChange={e => setPercent(e.target.value)}
                    style={{ width: "100%", padding: "8px 12px", fontSize: T.fsBase, color: T.dark, border: `1px solid ${T.borderDark}`, borderRadius: 6 }}
                    placeholder="e.g. 50"
                  />
                </div>
              </div>

              {error && <div style={{ color: T.red, fontSize: T.fsSm, marginBottom: 16 }}>{error}</div>}

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
                <button type="button" onClick={onClose} style={{ padding: "8px 16px", borderRadius: 6, fontSize: T.fsSm5, fontWeight: T.fwSemibold, background: "transparent", color: T.mid, border: "none", cursor: "pointer" }} disabled={loading}>Close</button>
                <button type="submit" style={{ padding: "8px 16px", borderRadius: 6, fontSize: T.fsSm5, fontWeight: T.fwSemibold, background: T.accent, color: T.white, border: "none", cursor: "pointer" }} disabled={loading}>
                  {loading ? "Saving..." : "Save Update"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </Modal>
  );
}
